import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import api from "@/lib/api";
import { MODULE_ICONS, STATUS_LABEL } from "@/lib/serviceModules";
import { Search, Star, Clock, Bike } from "lucide-react";

export default function CustomerHome() {
  const { t, lang } = useLang();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/service-modules").then(r => setModules(r.data));
    fetchRestaurants("");
  }, []);

  const fetchRestaurants = async (query) => {
    const { data } = await api.get("/restaurants", { params: query ? { q: query } : {} });
    setRestaurants(data);
  };

  const search = (e) => { e.preventDefault(); fetchRestaurants(q); };

  return (
    <CustomerLayout>
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <h1 className="text-3xl font-bold tracking-tight">What are you craving?</h1>
        <p className="text-[var(--so-text-soft)] mt-1">Discover restaurants and services near you.</p>

        <form onSubmit={search} className="mt-5 relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-4 w-4 h-4 text-[var(--so-text-soft)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("searchPh")}
            className="so-input ps-11"
            data-testid="customer-search"
          />
        </form>

        {/* Services strip */}
        <div className="mt-8">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] mb-3">Services</div>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
            {modules.map((m) => {
              const Icon = MODULE_ICONS[m.key];
              const label = lang === "ar" ? m.label_ar : m.label_en;
              const live = m.status === "live";
              const handleClick = () => {
                if (m.key === "restaurants") {
                  document.getElementById("restaurants-section")?.scrollIntoView({ behavior: "smooth" });
                } else {
                  navigate(`/app/service/${m.key}`);
                }
              };
              return (
                <button
                  key={m.key}
                  onClick={handleClick}
                  data-testid={`service-${m.key}`}
                  className="group flex flex-col items-center gap-2 p-3 rounded-2xl border border-[var(--so-border)] transition-all hover:border-[var(--so-orange)] cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${m.accent}1A`, color: m.accent }}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-semibold text-center">{label}</div>
                  {!live && <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--so-text-soft)]">{STATUS_LABEL[m.status][lang]}</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Featured restaurants */}
        <div id="restaurants-section" className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{t("featured")}</h2>
            <span className="text-sm text-[var(--so-text-soft)]">{restaurants.length} {t("restaurants").toLowerCase()}</span>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 so-stagger">
            {restaurants.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/app/restaurant/${r.id}`)}
                data-testid={`restaurant-card-${r.id}`}
                className="so-card overflow-hidden text-start"
              >
                <div className="h-40 bg-gray-200 dark:bg-gray-800 overflow-hidden">
                  <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-4">
                  <div className="font-bold text-lg">{r.name}</div>
                  <div className="text-xs text-[var(--so-text-soft)] mt-0.5">{r.cuisine}</div>
                  <div className="mt-3 flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> <b>{r.rating}</b> <span className="text-[var(--so-text-soft)]">({r.review_count})</span></span>
                    <span className="flex items-center gap-1 text-[var(--so-text-soft)]"><Clock className="w-3.5 h-3.5" /> {r.avg_prep_minutes}m</span>
                    <span className="flex items-center gap-1 text-[var(--so-text-soft)]"><Bike className="w-3.5 h-3.5" /> {formatPrice(r.delivery_fee)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
